import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthenticationService } from './services/authentication.service';
import { map, Observable, switchMap, take } from 'rxjs';
import { Router, RouterOutlet } from '@angular/router';
import { HeaderItem } from './models/header-item.model';
import { NavigationService } from './services/navigation.service';
import { FooterItem } from './models/footer-item.model';
import { GtmService } from './services/gtm.service';
import { environment } from '../environments/environment';
import { BrowserStorageService } from './services/browser-storage.service';
import { ElasticApmService } from './services/elastic-apm.service';
import { AsyncPipe, NgClass } from '@angular/common';
import { DesignSystemModule } from '@ukho/admiralty-angular';

@Component({
    selector: 'ukho-app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [NgClass, AsyncPipe, RouterOutlet, DesignSystemModule]
})
export class AppComponent implements OnInit {
  public isAuthenticated$: Observable<boolean>;
  public isValidUser$: Observable<boolean>;

  public isUduUser$: Observable<boolean | null>;

  public getIsAuthedInternalUser$: Observable<boolean>;
  public getIsAuthedExternalUser$: Observable<boolean>;

  public fullname: Observable<string | undefined>;

  public header: {
    title: string;
    logoImgUrl: string;
    logoLinkUrl: string;
    logoAltText: string;
    authOptions: {
      isSignedIn: () => boolean;
      signInHandler: () => void;
      signedInButtonText: string;
      signOutHandler: () => void;
      userProfileHandler: () => void;
    };
  } = {
    title: 'Data Upload',
    logoImgUrl: '../assets/UKHO_linear_logo.svg',
    logoLinkUrl: 'https://www.admiralty.co.uk/',
    logoAltText: 'UK Hydrographic Office',
    authOptions: {
      isSignedIn: () => this.isLoggedIn(),
      signInHandler: () => this.login(),
      signedInButtonText: 'Account',
      signOutHandler: () => this.logout(),
      userProfileHandler: () => this.authenticationService.editProfile()
    }
  };

  public footerItems: FooterItem[] = [];
  public headerItems: HeaderItem[] = [];

  private readonly destroyRef = inject(DestroyRef);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly navigationService = inject(NavigationService);
  private readonly gtmService = inject(GtmService);
  private readonly browserStorageService = inject(BrowserStorageService);
  private readonly elasticApmService = inject(ElasticApmService);

  constructor() {
    this.browserStorageService.initSessionStorage();
    this.browserStorageService.initLocalStorage();
    this.footerItems = this.navigationService.getFooter();
    this.isAuthenticated$ = this.authenticationService.isAuthenticated$();
    this.isValidUser$ = this.authenticationService.getIsValidUser();
    this.getIsAuthedInternalUser$ = this.authenticationService.getIsAuthedInternalUser();
    this.getIsAuthedExternalUser$ = this.authenticationService.getIsAuthedExternalUduUser();
    this.isUduUser$ = this.authenticationService.getIsUduUser();
    this.fullname = this.authenticationService.getFullName();
  }

  ngOnInit(): void {
    if (environment.production) {
      this.gtmService.initializeGtm();
    }

    document.addEventListener('securitypolicyviolation', (e: SecurityPolicyViolationEvent) => {
      this.elasticApmService.logError('csp-violation', e.blockedURI, {
        violatedDirective: e.violatedDirective,
        disposition: e.disposition,
        documentURI: e.documentURI,
        sourceFile: e.sourceFile ?? ''
      });
    });

    this.initializeBroadcast();
  }

  public initializeBroadcast(): void {
    this.authenticationService
      .initializeBroadcast()
      .pipe(
        switchMap(() => {
          return this.authenticationService.acquireTokenSilent();
        }),
        map(() => {
          this.authenticationService.setAccountDetails();
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.handleAuthNavigation();
        },
        error: () => {
          this.handleAuthNavigation();
        }
      });
  }

  public isLoggedIn(): boolean {
    return this.authenticationService.isAuthenticated();
  }

  public login(): void {
    localStorage.clear();
    this.authenticationService.login();
  }

  public logout(event?: Event) {
    if (!this.authenticationService.getNavigateAllowed()) {
      event?.preventDefault();
      const confirmed = confirm("If you leave this page, your survey won't be saved and can't be recovered.");

      if (confirmed) {
        this.authenticationService.logout();
      }
    } else {
      this.authenticationService.logout();
    }
  }

  public onAccountClicked(event: Event): void {
    if (!this.authenticationService.getNavigateAllowed()) {
      event.preventDefault();
      const confirmed = confirm("If you leave this page, your survey won't be saved and can't be recovered.");

      if (confirmed) {
        this.authenticationService.editProfile();
      }
    } else {
      this.authenticationService.editProfile();
    }
  }

  public sendEmail(): string {
    const recipient = 'products.feedback@UKHO.gov.uk';
    const subject = 'UKHO Data Upload';
    return `mailto:${recipient}?subject=${encodeURIComponent(subject)}`;
  }

  public navigate(routerLink: string) {
    this.browserStorageService.setSessionStorageData('form', null);
    this.browserStorageService.setSessionStorageData('formModified', null);
    this.browserStorageService.setSessionStorageData('surveyId', null);
    this.router.navigate([routerLink]);
  }

  public dashboardNavigate(): void {
    this.authenticationService
      .getIsAuthedInternalUserWhenReady()
      .pipe(
        take(1),
        map((isInternal) => this.router.navigate([isInternal ? '/internal/dashboard' : '/external/dashboard'])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public handleAuthNavigation(): void {
    const isInternalUser: boolean | null = this.authenticationService.getIsInternalUserValue();
    this.headerItems = isInternalUser
      ? this.navigationService.getInternalHeader(this.router)
      : this.navigationService.getExternalHeader(this.router);
  }
}
